import type { FieldSpec } from '@/modules/protocols/components/protocol-fields-modal';
import type { HelpdeskAgent } from '@/services/escalation-policy.service';
import type { AccountUser } from '@/types';

/**
 * A brand's escalation policy: the default each escalation type falls back to. A protocol step
 * or instruction action takes any field it leaves blank from here, so a blank field is only
 * missing when the policy does not set it either. Mirrors kim-ai-backend's escalationPolicy.utils.
 */

export const ESCALATION_FIELDS = {
  'send-email': ['to', 'cc', 'bcc', 'subject', 'body'],
  // The policy holds a default for both targets; a step uses only its own target's.
  assign: ['agentId', 'agentName', 'agentEmail', 'agentProvider', 'sentinelUserId', 'sentinelUserName', 'sentinelUserEmail'],
  // Unassign's only default is who it acts on, for a step that leaves its target blank.
  unassign: ['assignTarget'],
  'send-private-note': ['noteContent'],
  'send-slack-message': ['slackChannel', 'slackMessage']
} as const;

export type EscalationType = keyof typeof ESCALATION_FIELDS;

export type EscalationPolicy = Partial<Record<EscalationType, Record<string, string>>>;

export const ESCALATION_TYPES = Object.keys(ESCALATION_FIELDS) as EscalationType[];

/** The types the brand policy holds a default for. */
export const POLICY_ESCALATION_TYPES = ESCALATION_TYPES.filter((type) => ESCALATION_FIELDS[type].length > 0);

export const ESCALATION_TYPE_LABELS: Record<EscalationType, string> = {
  'send-email': 'Send Email',
  assign: 'Assign',
  unassign: 'Unassign',
  'send-private-note': 'Send Private Note',
  'send-slack-message': 'Send Slack Message'
};

/** Who an Assign or Unassign acts on: the helpdesk ticket's agent, or the Sentinel owner in Kim. */
export type AssignTarget = 'agent' | 'sentinel';

export const ASSIGN_TARGET_LABELS: Record<AssignTarget, string> = { agent: 'Agent', sentinel: 'Sentinel' };

/** A step saved before targets existed (or by the legacy Assign to User) assigns a helpdesk agent. */
export const assignTargetOf = (attrs: Record<string, unknown>): AssignTarget =>
  attrs['assignTarget'] === 'sentinel' ? 'sentinel' : 'agent';

/** An Unassign step's target: blank ('') leaves it to the brand policy. */
export type UnassignTarget = AssignTarget | '';

export const BRAND_DEFAULT_LABEL = 'Brand default';

/** An Unassign step's own target, kept blank when it names none so the brand policy decides. */
export const unassignTargetOf = (attrs: Record<string, unknown>): UnassignTarget =>
  attrs['assignTarget'] === 'sentinel' || attrs['assignTarget'] === 'agent' ? attrs['assignTarget'] : '';

/** Who an Unassign acts on: the step's own target wins, a blank one takes the policy's, neither means the agent. */
export const resolveUnassignTarget = (attrs: Record<string, unknown>, policy: EscalationPolicy | null | undefined): AssignTarget =>
  unassignTargetOf(attrs) || unassignTargetOf(policy?.unassign ?? {}) || 'agent';

const AGENT_FIELDS = ['agentId', 'agentName', 'agentEmail', 'agentProvider'] as const;
const SENTINEL_FIELDS = ['sentinelUserId', 'sentinelUserName', 'sentinelUserEmail'] as const;

export const ASSIGN_TARGET_FIELDS: Record<AssignTarget, readonly string[]> = { agent: AGENT_FIELDS, sentinel: SENTINEL_FIELDS };

const REQUIRED_FIELDS: Record<EscalationType, readonly string[]> = {
  'send-email': ['to', 'subject', 'body'],
  assign: [],
  unassign: [],
  'send-private-note': ['noteContent'],
  'send-slack-message': ['slackChannel', 'slackMessage']
};

export const FIELD_LABELS: Record<string, string> = {
  to: 'recipient (To)',
  cc: 'CC',
  bcc: 'BCC',
  subject: 'subject',
  body: 'message',
  agentId: 'agent',
  agentName: 'agent',
  agentEmail: 'agent email',
  sentinelUserId: 'Sentinel',
  sentinelUserName: 'Sentinel',
  sentinelUserEmail: 'Sentinel email',
  assignTarget: 'default target',
  noteContent: 'note text',
  slackChannel: 'Slack channel',
  slackMessage: 'Slack message'
};

const blank = (value: unknown) =>
  typeof value !== 'string' || !value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

/** The fields a step of this type reads; an Assign reads only its own target's. */
const stepFields = (type: EscalationType, attrs: Record<string, unknown>): readonly string[] =>
  type === 'assign' ? ASSIGN_TARGET_FIELDS[assignTargetOf(attrs)] : ESCALATION_FIELDS[type];

/** Whether an Assign step names its own person, so none of the policy's is mixed in. */
const ownsAssignee = (attrs: Record<string, unknown>): boolean =>
  assignTargetOf(attrs) === 'sentinel'
    ? !blank(attrs['sentinelUserId'])
    : !blank(attrs['agentId']) || !blank(attrs['agentEmail']);

/** The step's fields with each blank one filled from the policy; a picked person is never mixed with the policy's. */
export function applyEscalationPolicy(
  type: EscalationType,
  attrs: Record<string, unknown>,
  policy: EscalationPolicy | null | undefined
): Record<string, string> {
  const fallback = policy?.[type] ?? {};
  const source = type === 'assign' && ownsAssignee(attrs) ? {} : fallback;
  const merged: Record<string, string> = {};
  for (const field of stepFields(type, attrs)) {
    const value = blank(attrs[field]) ? source[field] : (attrs[field] as string);
    if (!blank(value)) merged[field] = value as string;
  }
  return merged;
}

/** Required fields neither the step nor the policy sets, as a person reads them. */
export function missingEscalationFields(
  type: EscalationType,
  attrs: Record<string, unknown>,
  policy: EscalationPolicy | null | undefined
): string[] {
  const merged = applyEscalationPolicy(type, attrs, policy);
  if (type === 'assign') {
    if (assignTargetOf(attrs) === 'sentinel') return blank(merged['sentinelUserId']) ? ['Sentinel'] : [];
    // An agent is picked from the helpdesk's list (an id) or typed in (an email): either will do.
    return blank(merged['agentId']) && blank(merged['agentEmail']) ? ['agent'] : [];
  }
  return REQUIRED_FIELDS[type].filter((field) => blank(merged[field])).map((field) => FIELD_LABELS[field] ?? field);
}

/** The Send Slack Message composer's fields, in protocols and in the escalation policy. */
export const SLACK_FIELDS = [
  { key: 'slackChannel', label: 'Channel', placeholder: 'support-escalations' },
  {
    key: 'slackMessage',
    label: 'Message',
    placeholder: 'Write the message… {{TICKET_URL}} and {{CONTEXT}} are filled in when it is sent.',
    multiline: true
  }
];

/** Whether the policy sets anything for this type. */
export const hasPolicy = (policy: EscalationPolicy | null | undefined, type: EscalationType) =>
  Object.values(policy?.[type] ?? {}).some((value) => !blank(value));

/* ── Assign, target agent: a helpdesk agent from its list, or any agent by email ── */

export const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const agentKey = (provider?: string, id?: string) => (id ? `${provider ?? ''}:${id}` : '');

/**
 * The Assign composer for an agent: the helpdesk's agents where it lists them, and an email for any
 * agent it does not (or a helpdesk that lists none). `noneLabel` is what the empty pick means.
 */
export function assignFields(
  agents: HelpdeskAgent[],
  noneLabel: string,
  /** The agent saved now: kept as an option when the list does not have it (still loading, a Kim user, gone). */
  current: Record<string, string | undefined> = {}
): FieldSpec[] {
  const currentKey = agentKey(current['agentProvider'], current['agentId']);
  const unlisted = currentKey && !agents.some((agent) => agentKey(agent.provider, agent.id) === currentKey);
  return [
    {
      key: 'agent',
      label: 'Agent',
      options: [
        { value: '', label: noneLabel },
        ...(unlisted ? [{ value: currentKey, label: `${current['agentName'] || current['agentEmail'] || current['agentId']} (current)` }] : []),
        ...agents.map((agent) => ({
          value: agentKey(agent.provider, agent.id),
          label: `${agent.name}${agent.email && agent.email !== agent.name ? ` (${agent.email})` : ''} · ${capitalize(agent.provider)}`
        }))
      ]
    },
    { key: 'email', label: 'Or email', placeholder: 'agent@brand.com — assigns the helpdesk agent with this email' }
  ];
}

/** The composer's values for a saved assignee. */
export const assignValues = (attrs: Record<string, string | undefined>): Record<string, string> => ({
  agent: agentKey(attrs['agentProvider'], attrs['agentId']),
  email: attrs['agentId'] ? '' : (attrs['agentEmail'] ?? '')
});

/** A typed email wins over the pick; the saved agent stays as it is; nothing chosen leaves every field empty. */
export function assignFromValues(
  values: Record<string, string>,
  agents: HelpdeskAgent[],
  current: Record<string, string | undefined> = {}
): Record<string, string> {
  const email = (values['email'] ?? '').trim();
  if (email) return { agentId: '', agentName: email, agentEmail: email, agentProvider: '' };
  if (values['agent'] && values['agent'] === agentKey(current['agentProvider'], current['agentId'])) {
    return {
      agentId: current['agentId'] ?? '',
      agentName: current['agentName'] ?? '',
      agentEmail: current['agentEmail'] ?? '',
      agentProvider: current['agentProvider'] ?? ''
    };
  }
  const agent = agents.find((entry) => agentKey(entry.provider, entry.id) === values['agent']);
  return agent
    ? { agentId: agent.id, agentName: agent.name, agentEmail: agent.email, agentProvider: agent.provider }
    : { agentId: '', agentName: '', agentEmail: '', agentProvider: '' };
}

export const assignEmailRefusal = (values: Record<string, string>): string | null => {
  const email = (values['email'] ?? '').trim();
  return email && !isEmail(email) ? `Not a valid email: ${email}` : null;
};

/* ── Assign, target Sentinel: a Kim user with the Sentinel role ── */

/** The account's users who can own a Sentinel ticket. */
export const sentinelUsers = (users: AccountUser[]): AccountUser[] => users.filter((user) => user.role === 'sentinel');

/**
 * The Assign composer for a Sentinel. `noneLabel` is what the empty pick means; the Sentinel saved
 * now stays an option when the list does not have it (still loading, role changed, removed).
 */
export function sentinelFields(
  sentinels: AccountUser[],
  noneLabel: string,
  current: Record<string, string | undefined> = {}
): FieldSpec[] {
  const currentId = current['sentinelUserId'] ?? '';
  const unlisted = currentId && !sentinels.some((user) => String(user.id) === currentId);
  return [
    {
      key: 'sentinel',
      label: 'Sentinel',
      options: [
        { value: '', label: noneLabel },
        ...(unlisted ? [{ value: currentId, label: `${current['sentinelUserName'] || current['sentinelUserEmail'] || currentId} (current)` }] : []),
        ...sentinels.map((user) => ({
          value: String(user.id),
          label: `${user.name}${user.email && user.email !== user.name ? ` (${user.email})` : ''}`
        }))
      ]
    }
  ];
}

/** The composer's values for a saved Sentinel. */
export const sentinelValues = (attrs: Record<string, string | undefined>): Record<string, string> => ({
  sentinel: attrs['sentinelUserId'] ?? ''
});

/** The saved Sentinel stays as it is; nothing chosen leaves every field empty. */
export function sentinelFromValues(
  values: Record<string, string>,
  sentinels: AccountUser[],
  current: Record<string, string | undefined> = {}
): Record<string, string> {
  const id = values['sentinel'] ?? '';
  if (id && id === current['sentinelUserId']) {
    return { sentinelUserId: id, sentinelUserName: current['sentinelUserName'] ?? '', sentinelUserEmail: current['sentinelUserEmail'] ?? '' };
  }
  const user = sentinels.find((entry) => String(entry.id) === id);
  return user
    ? { sentinelUserId: String(user.id), sentinelUserName: user.name, sentinelUserEmail: user.email }
    : { sentinelUserId: '', sentinelUserName: '', sentinelUserEmail: '' };
}

/* ── An Unassign step: its target, or the brand's default ── */

/** The Unassign step's Target pick: Agent, Sentinel, or the brand policy's default. */
export const UNASSIGN_TARGET_FIELD: FieldSpec = {
  key: 'target',
  label: 'Target',
  options: [
    { value: 'agent', label: ASSIGN_TARGET_LABELS.agent },
    { value: 'sentinel', label: ASSIGN_TARGET_LABELS.sentinel },
    { value: '', label: BRAND_DEFAULT_LABEL }
  ]
};

/** The brand policy's Unassign composer: a default target, or none. */
export const UNASSIGN_POLICY_FIELD: FieldSpec = {
  key: 'assignTarget',
  label: 'Default target',
  options: [
    { value: '', label: 'Not set' },
    { value: 'agent', label: ASSIGN_TARGET_LABELS.agent },
    { value: 'sentinel', label: ASSIGN_TARGET_LABELS.sentinel }
  ]
};

/* ── An Assign step: the target, then the person for that target ── */

/** The Assign step's Target pick. */
export const ASSIGN_TARGET_FIELD: FieldSpec = {
  key: 'target',
  label: 'Target',
  options: [
    { value: 'agent', label: ASSIGN_TARGET_LABELS.agent },
    { value: 'sentinel', label: ASSIGN_TARGET_LABELS.sentinel }
  ]
};

const whileTarget = (target: AssignTarget) => (field: FieldSpec): FieldSpec => ({ ...field, showWhen: { key: 'target', value: target } });

/** The Assign step composer: the target, and the person picker for whichever target is chosen. */
export const assignStepFields = (
  agents: HelpdeskAgent[],
  sentinels: AccountUser[],
  noneLabel: string,
  current: Record<string, string | undefined> = {}
): FieldSpec[] => [
  ASSIGN_TARGET_FIELD,
  ...assignFields(agents, noneLabel, current).map(whileTarget('agent')),
  ...sentinelFields(sentinels, noneLabel, current).map(whileTarget('sentinel'))
];

export const assignStepValues = (attrs: Record<string, string | undefined>): Record<string, string> => ({
  target: assignTargetOf(attrs),
  ...assignValues(attrs),
  ...sentinelValues(attrs)
});

/** The step's attrs: its target's person, and the other target's fields cleared so they never linger. */
export function assignStepFromValues(
  values: Record<string, string>,
  agents: HelpdeskAgent[],
  sentinels: AccountUser[],
  current: Record<string, string | undefined> = {}
): Record<string, string> {
  const target: AssignTarget = values['target'] === 'sentinel' ? 'sentinel' : 'agent';
  const agent = target === 'agent' ? assignFromValues(values, agents, current) : assignFromValues({}, []);
  const sentinel = target === 'sentinel' ? sentinelFromValues(values, sentinels, current) : sentinelFromValues({}, []);
  return { escalationType: 'assign', assignTarget: target, ...agent, ...sentinel };
}
